import { ElementIcon } from "@/components/ui/icons";
import {
  setHoveredConnectionId,
  unsetHoveredConnectionId,
} from "@/data-preparation-tool/store/data-preparation-ui/data-preparation-ui-slice";
import {
  selectRevisionEntityById,
  selectRootEntity,
} from "@/data-preparation-tool/store/revision/revision-entity-selectors";
import { selectRelevantGlobalEdges } from "@/data-preparation-tool/store/select-active-revision-edges";
import { useElementName } from "@/data-preparation-tool/utils/entity-utils";
import { IconText } from "@/registration-tools/common/icon-text";
import { MultiCloudRegistrationMetrics } from "@/registration-tools/common/registration-report/multicloud-registration-metrics";
import { RegistrationCard } from "@/registration-tools/common/registration-report/registration-card";
import { QualityIcon } from "@/registration-tools/common/registration-report/registration-metrics";
import { useThresholdSetContext } from "@/registration-tools/common/registration-report/threshold-set-context";
import { ThresholdSetSwitch } from "@/registration-tools/common/registration-report/threshold-set-switch";
import {
  QualityStatus,
  RegistrationDetails,
} from "@/registration-tools/utils/metrics";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { FaroText, NoTranslate, TranslateVar, grey } from "@faro-lotv/flat-ui";
import { GUID } from "@faro-lotv/foundation";
import { RegistrationEdgeRevision } from "@faro-lotv/service-wires";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Divider, ListItemButton, SvgIcon } from "@mui/material";
import { Stack } from "@mui/system";
import { useMemo, useState, type JSX } from "react";
import { ReactComponent as Connection } from "../../../registration-tools/icons/connections.svg";
import { ReactComponent as RegistrationConnections } from "../../../registration-tools/icons/metrics-connections-graphic.svg";
import { ConnectionQualityDialog } from "../data-preparation-dialogs/data-preparation-connection-quality-dialog";
import { iconForCaptureTreeElement } from "../scan-tree/entity-icon";

/**
 * @returns A view displaying the report for a multi-cloud registration.
 */
export function CaptureTreeRegistrationReportView(): JSX.Element | undefined {
  const rootEntity = useAppSelector(selectRootEntity);
  const globalEdges = useAppSelector(selectRelevantGlobalEdges);
  const groupedEdgesBySourceScan = useMemo(() => {
    const groupedBySource = new Map<GUID, RegistrationEdgeRevision[]>();
    for (const edge of globalEdges) {
      const group = groupedBySource.get(edge.sourceId) ?? [];
      group.push(edge);
      groupedBySource.set(edge.sourceId, group);
    }
    return groupedBySource;
  }, [globalEdges]);

  if (!rootEntity?.report) {
    return;
  }
  const { report } = rootEntity;
  const { avgPointError, maxPointError, minOverlap } = report.cloudToCloud;

  return (
    <Stack spacing={1} sx={{ alignItems: "center" }}>
      <ThresholdSetSwitch />
      <MultiCloudRegistrationMetrics
        averagePointError={avgPointError}
        maxPointError={maxPointError}
        minOverlap={minOverlap}
      />
      <RegistrationCard
        title="Connections"
        cardIcon={
          <SvgIcon
            component={RegistrationConnections}
            inheritViewBox
            sx={{ width: "100%", height: "100%" }}
          />
        }
      >
        <FaroText variant="helpText">
          Here you can see how many and which connections with other point
          clouds were calculated for each point cloud. Click on a connection to
          see the details page for each pair.
        </FaroText>
        <Stack divider={<Divider flexItem />}>
          {Array.from(groupedEdgesBySourceScan.entries()).map(
            ([sourceId, edges]) => (
              <ConnectionView id={sourceId} key={sourceId} edges={edges} />
            ),
          )}
        </Stack>
      </RegistrationCard>
    </Stack>
  );
}

type ConnectionViewProps = {
  /** The scan id of the source */
  id: GUID;
  /** The edges corresponding to the source scan id */
  edges: RegistrationEdgeRevision[];
};

/**
 * @returns View for registrations result of a connection.
 */
function ConnectionView({ id, edges }: ConnectionViewProps): JSX.Element {
  const name = useElementName(id);
  const scan = useAppSelector(selectRevisionEntityById(id));
  const icon = useMemo(() => iconForCaptureTreeElement(scan), [scan]);
  return (
    <Stack sx={{ gap: 0.5 }}>
      <Stack
        direction="row"
        sx={{ alignItems: "flex-end", justifyContent: "space-between" }}
      >
        <IconText
          icon={<ElementIcon icon={icon} />}
          variant="heading14"
          text={<NoTranslate>{name}</NoTranslate>}
        />
        <FaroText
          variant="heading14"
          sx={{ color: "info.main", padding: "0.5em" }}
        >
          <TranslateVar name="connectionCount">{edges.length}</TranslateVar>{" "}
          {edges.length === 1 ? "Connection" : "Connections"}
        </FaroText>
      </Stack>
      <Stack direction="column" sx={{ pl: 3 }}>
        {edges.map((edge) => (
          <ConnectionRegistrationView {...edge} key={edge.id} />
        ))}
      </Stack>
    </Stack>
  );
}

/**
 * @param edge the registration edge
 * @returns View for a single cloud-to-cloud registration, i.e. the connection between two scans.
 */
function ConnectionRegistrationView(
  edge: RegistrationEdgeRevision,
): JSX.Element {
  const dispatch = useAppDispatch();
  const { id, targetId, data } = edge;
  const targetScanName = useElementName(targetId);

  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);

  const { thresholdSet } = useThresholdSetContext();

  return (
    <>
      <ListItemButton
        title="View connection details"
        onClick={() => setIsDetailsDialogOpen(true)}
        onMouseEnter={() => {
          dispatch(setHoveredConnectionId(id));
        }}
        onMouseLeave={() => {
          dispatch(unsetHoveredConnectionId(id));
        }}
        sx={{ px: 1, py: 0.5 }}
      >
        <Stack
          direction="row"
          sx={{
            columnGap: 1,
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Stack direction="row" sx={{ alignItems: "center", columnGap: 1 }}>
            <SvgIcon
              component={Connection}
              inheritViewBox
              sx={{
                width: "1em",
                height: "1em",
                color: grey[400],
              }}
            />
            <NoTranslate>
              <FaroText
                aria-label="connection"
                variant="heading14"
                color={grey[600]}
              >
                {targetScanName}
              </FaroText>
            </NoTranslate>
          </Stack>
          <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
            <QualityIcon
              quality={
                data?.metrics?.rlyHistogram
                  ? new RegistrationDetails(
                      data.metrics.overlap,
                      thresholdSet,
                      data.metrics.rlyHistogram,
                    ).registrationQuality
                  : QualityStatus.UNKNOWN
              }
              width="0.75em"
              height="0.75em"
            />
            <KeyboardArrowRightIcon style={{ fill: grey[400] }} />
          </Stack>
        </Stack>
      </ListItemButton>
      <ConnectionQualityDialog
        registrationEdge={edge}
        isDialogOpen={isDetailsDialogOpen}
        onDialogClose={setIsDetailsDialogOpen}
      />
    </>
  );
}
